import React, { useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import './App.css';
import Nav from './components/Nav';
import Employees from './components/Employees';
import Dashboard from './components/Dashboard';
import Letters from './components/Letters';
import MyLetters from './components/MyLetters';
import Requests from './components/Requests';
import Forms from './components/Forms';
import PayrollNotes from './components/PayrollNotes';
import PayrollNotesCalendar from './components/PayrollNotesCalendar';
import EmployeeForms from './components/EmployeeForms/employeeForms';
import Login from './components/Login';
import ProtectedRoute from "./components/ProtectedRoute";
import { UserProvider } from './context/UserContext';
import { AuthContext } from './context/AuthContext';
import './App.css';
import ReturnPage from './components/ReturnPage';
import FormComplete from './components/FormComplete';
import ManagerDocumentReview from './components/EmployeeForms/ManagerDocumentReview';
import IncompleteFormsTracker from './components/IncompleFormsTracker';
import WebAuthnAuth from './components/WebAuthnAuth';
import FirstTimeSetup from './components/FirstTimeSetup';
import ImpersonationBar from './components/ImpersonationBar';
import Audits from './pages/Audits';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import PayChangeAcknowledgement from './components/PayChangeAcknowledgement';
import SchedulingCalendar from './components/SchedulingCalendar';
import MySchedule from './components/MySchedule';
import FormsVNext from './components/FormsVNext';
import AvailabilityPage from './components/AvailabilityPage';
import StaffingRequirements from './components/StaffingRequirements';
import FormsV2Dashboard from './components/v2/FormsV2Dashboard';
import FormBuilder from './components/v2/FormBuilder';
import FormBuilderDragDrop from './components/v2/FormBuilderDragDrop';
import FormBuilderAdvanced from './components/v2/FormBuilderAdvanced';
import FormRenderer from './components/v2/FormRenderer';

// Layout wrapper to handle conditional navigation
function AppLayout({ children }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useContext(AuthContext);
  const { hideNavigation } = useNavigation();
  const [isImpersonating, setIsImpersonating] = useState(false);
  
  // Check if impersonating
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsImpersonating(payload.isImpersonating || false);
      } catch (e) {
        setIsImpersonating(false);
      }
    }
  }, [isAuthenticated]);
  
  return (
    <>
      {/* Nav Container */}
      {isAuthenticated && !hideNavigation && (
        <Box 
          sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: { xs: '48px', md: '64px' }
          }}
        >
          <Nav />
        </Box>
      )}
      
      {/* Impersonation Bar */}
      {!hideNavigation && <ImpersonationBar />}
      
      {/* Main content */}
      <Box 
        component="main" 
        sx={{
          flexGrow: 1,
          pt: hideNavigation ? 0 : (isAuthenticated ? (isImpersonating ? { xs: '114px', sm: '102px' } : { xs: '64px', sm: '85px' }) : 0),
          pb: 0,
          width: '100%',
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        {children}
      </Box>
    </>
  );
}

function App() {
  const [selectedStore, setSelectedStore] = useState('All');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showAcknowledgement, setShowAcknowledgement] = useState(false);
  const { isAuthenticated, userRole, isLoading, userDetails } = useContext(AuthContext);
  
  console.log('App render - Auth state:', { isAuthenticated, userRole, isLoading });

  // Do not auto-open acknowledgement modal; Dashboard shows banner instead

  // Show loading state while authentication is being determined
  if (isLoading) {
    return <div>Loading authentication state...</div>;
  }

  // Determine the logo link based on the user's role
  const logoLink = userRole === 'user' ? '/employee_forms' : '/dashboard';

  const handleStoreChange = (storeName) => {
    setSelectedStore(storeName);
  };

  const handleProfileOpen = (isOpen) => {
    setIsProfileOpen(isOpen);
  };

  return (
    <>
    <Router>
      <NavigationProvider>
        <AppLayout>
          <UserProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/first-time-setup" element={<FirstTimeSetup />} />
            <Route path="/form-complete" element={<FormComplete />} />
            <Route path="/webauthn-auth" element={<WebAuthnAuth />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/manager/documents" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <ManagerDocumentReview />
                </ProtectedRoute>
              }
            />
            {/* New Route for Incomplete Forms Tracker */}
            <Route 
              path="/incomplete-forms" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <IncompleteFormsTracker />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employees"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Employees selectedStore={selectedStore} setProfileOpen={handleProfileOpen} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letters"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Letters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-letters"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <MyLetters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests"
              element={
                <ProtectedRoute allowedRoles={['admin', 'user', 'manager']}>
                  <Requests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checks"
              element={
                <ProtectedRoute allowedRoles={['admin', 'user', 'manager']}>
                  <Requests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forms"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <Forms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll_notes"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <PayrollNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll_notes_calendar"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <PayrollNotesCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee_forms"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <EmployeeForms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scheduling"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <SchedulingCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-schedule"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <MySchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/availability"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <AvailabilityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staffing-requirements"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <StaffingRequirements />
                </ProtectedRoute>
              }
            />

            {/* vNext Forms scaffold */}
            <Route
              path="/vnext/forms"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <FormsVNext />
                </ProtectedRoute>
              }
            />

            {/* V2 Forms System */}
            <Route
              path="/v2/forms"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <FormsV2Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/v2/form-builder"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <FormBuilderAdvanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/v2/form-builder/:id"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <FormBuilderAdvanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/v2/form-builder-basic"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <FormBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/v2/forms/:id"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <FormRenderer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/v2/forms/legacy/:id"
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
                  <FormRenderer />
                </ProtectedRoute>
              }
            />

            {/* Hidden admin-only audit page */}
            <Route
              path="/audits"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Audits />
                </ProtectedRoute>
              }
            />

            {/* Catch-all Route with role-based redirect */}
            <Route path="*" element={<NotFound />} />
            <Route path="/return" element={<ReturnPage />} />
          </Routes>
          </UserProvider>
        </AppLayout>
      </NavigationProvider>
    </Router>
    
    {/* Pay Change Acknowledgement Modal */}
    <PayChangeAcknowledgement 
      open={showAcknowledgement} 
      onClose={() => setShowAcknowledgement(false)} 
    />
    </>
  );
}

// NotFound component with role-based redirect
const NotFound = () => {
  const { userRole } = useContext(AuthContext);

  if (userRole === 'user') {
    return <Navigate to="/employee_forms" replace />;
  }

  return (
    <Box sx={{ textAlign: 'center', mt: 8 }}>
      <h1>🛑</h1>
      <p>You are not allowed to view this page</p>
    </Box>
  );
};

export default App;
